<div class="modal-header">
  <div class="modal-title">
    <h3 class="mb-0">
      {{ ESignTemplate?.id ? 'Edit ESign template' : 'Add ESign template' }}
    </h3>
    <p class="modal-subtitle mb-0">
      Map a client to an ESign provider template so signature flows pull the
      right document.
    </p>
  </div>
  <button
    type="button"
    class="btn-close"
    aria-label="Close"
    (click)="close('cross close')"
  ></button>
</div>

<div class="modal-body">
  <form [formGroup]="ESignTemplateForm">
    <div class="row g-3">
      <div class="col-md-4">
        <div class="form-group mb-0">
          <label class="form-label" for="aet-provider">ESign provider</label>
          <select
            id="aet-provider"
            class="form-select"
            formControlName="ESignProviderId"
            (change)="onESignProviderChange()"
          >
            <option [ngValue]="null" disabled>Pick one…</option>
            @for (ESignProvider of ESignProviders; track ESignProvider) {
              <option [ngValue]="ESignProvider.id">
                {{ ESignProvider.name }}
              </option>
            }
          </select>
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-group mb-0">
          <label class="form-label" for="aet-client">Client</label>
          <select
            id="aet-client"
            class="form-select"
            formControlName="CompanyId"
          >
            <option [ngValue]="null" disabled>Pick one…</option>
            @for (Company of Companies; track Company) {
              <option [ngValue]="Company.id">
                {{ Company.legalName }}
              </option>
            }
          </select>
        </div>
      </div>

      <div class="col-md-4">
        <div class="form-group mb-0">
          <label class="form-label" for="aet-templateId">Template ID</label>
          <input
            id="aet-templateId"
            type="text"
            class="form-control"
            formControlName="TemplateId"
            placeholder="Provider template UUID"
          />
        </div>
      </div>

      @if (isViveProvider) {
        <div class="col-md-4">
          <div class="form-group mb-0">
            <label class="form-label" for="aet-templateType">
              Template type
            </label>
            <select
              id="aet-templateType"
              class="form-select"
              formControlName="templateType"
            >
              <option [ngValue]="null" disabled>Pick one…</option>
              @for (templateType of templateTypes; track templateType) {
                <option [ngValue]="templateType.value">
                  {{ templateType.display }}
                </option>
              }
            </select>
          </div>
        </div>
      }
    </div>
  </form>

  @if (ESignTemplateForm.controls.TemplateFile) {
    <hr class="aet-divider" />
    <div class="form-group mb-0">
      <label class="form-label">Template file</label>
      @if (ESignTemplate?.id && currentTemplateFile) {
        <div class="aet-current-file">
          <div class="aet-current-file-details">
            <span class="aet-current-file-label">Current file</span>
            <span class="aet-current-file-name">
              {{ currentTemplateFile.filename }}
            </span>
            <span class="aet-current-file-meta">
              Published {{ currentTemplateFile.publishedAt | date: 'MM/dd/y' }}
              @if (currentTemplateFile.publishedByName) {
                by {{ currentTemplateFile.publishedByName }}
              }
            </span>
          </div>
          <button
            type="button"
            class="btn btn-ghost"
            [disabled]="isDownloadingTemplateFile"
            (click)="
              downloadTemplateFileVersion(currentTemplateFile.TemplateFileId)
            "
          >
            <i class="bi bi-download" aria-hidden="true"></i>
            Download
          </button>
        </div>
      }
      @if (ESignTemplate?.id && previousTemplateFileVersions.length > 0) {
        <div class="aet-previous-files">
          <button
            type="button"
            class="aet-previous-files-toggle"
            aria-controls="aet-previous-files-list"
            [attr.aria-expanded]="isPreviousVersionsOpen"
            (click)="isPreviousVersionsOpen = !isPreviousVersionsOpen"
          >
            <span class="aet-current-file-label">
              Previous versions ({{ previousTemplateFileVersions.length }})
            </span>
            <i
              class="bi bi-chevron-{{ isPreviousVersionsOpen ? 'up' : 'down' }}"
              aria-hidden="true"
            ></i>
          </button>
          @if (isPreviousVersionsOpen) {
            <div id="aet-previous-files-list">
              @for (
                version of previousTemplateFileVersions;
                track version.TemplateFileId
              ) {
                <div class="aet-previous-file">
                  <div class="aet-current-file-details">
                    <span class="aet-current-file-name">
                      {{ version.filename }}
                    </span>
                    <span class="aet-current-file-meta">
                      Published {{ version.publishedAt | date: 'MM/dd/y' }}
                      @if (version.publishedByName) {
                        by {{ version.publishedByName }}
                      }
                    </span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-ghost"
                    [disabled]="isDownloadingTemplateFile"
                    (click)="
                      downloadTemplateFileVersion(version.TemplateFileId)
                    "
                  >
                    <i class="bi bi-download" aria-hidden="true"></i>
                    Download
                  </button>
                </div>
              }
            </div>
          }
        </div>
      }
      @if (ESignTemplate?.id) {
        <p class="aet-file-hint">
          Uploading a file publishes it as the new active version of this
          template. Vendors who signed the current version stay compliant until
          they are asked to re-sign at their next renewal. Leave this empty to
          change the details only.
        </p>
      }
      <app-file-upload-file-display
        [Files]="Files"
        [hideSelectFileButton]="true"
        (FileEvent)="fileUpload($event)"
        (removeFileEvent)="removeFile($event)"
      ></app-file-upload-file-display>
    </div>

    <hr class="aet-divider" />

    <div class="form-group mb-0">
      <label class="form-label">Field mapping</label>

      @if (!ESignTemplate?.id) {
        <p class="aet-file-hint mb-0">
          Save this template first, then reopen it to map the fields in its PDF.
          VIVE reads the form fields from the stored file.
        </p>
      } @else if (acroFormFieldsLoading) {
        <p class="aet-file-hint mb-0">Reading this PDF’s form fields…</p>
      } @else if (acroFormFieldsError) {
        <div class="alert alert-danger mb-0">{{ acroFormFieldsError }}</div>
      } @else if (fields.length === 0) {
        <p class="aet-file-hint mb-0">
          This PDF declares no form fields, so there is nothing to map. VIVE
          fills it with its standard field handling.
        </p>
      } @else {
        <!--
          Both failure directions, stated before the table rather than left for
          the admin to spot. Unmapped fields print blank on every signed
          agreement; a mapping with no field behind it never lands at all.
        -->
        @if (unmappedFieldCount > 0) {
          <div class="alert alert-warning">
            {{ unmappedFieldCount }}
            {{ unmappedFieldCount === 1 ? 'field has' : 'fields have' }}
            nothing to fill
            {{ unmappedFieldCount === 1 ? 'it' : 'them' }}. They will print
            blank on every signed agreement.
          </div>
        }

        @if (missingFieldCount > 0) {
          <div class="alert alert-danger">
            {{ missingFieldCount }}
            {{ missingFieldCount === 1 ? 'mapping points' : 'mappings point' }}
            at a field that is no longer in this PDF — most likely renamed.
            Re-point {{ missingFieldCount === 1 ? 'it' : 'them' }} below, or set
            {{ missingFieldCount === 1 ? 'it' : 'them' }} to leave blank.
          </div>
        }

        @if (unlabelledOptionFieldNames.length > 0) {
          <div class="alert alert-warning">
            {{ unlabelledOptionFieldNames.join(', ') }}
            {{
              unlabelledOptionFieldNames.length === 1
                ? 'has a choice'
                : 'have choices'
            }}
            nobody has named yet. The vendor will be shown the PDF's own value —
            something like <code>Choice1</code> — as the thing they are agreeing
            to. You can still save, so an unrelated edit is not held up, but
            name them before this template goes to a vendor.
          </div>
        }

        @if (piiFieldNames.length > 0) {
          <div class="alert alert-info">
            Heads up — {{ piiFieldNames.join(', ') }}
            {{ piiFieldNames.length === 1 ? 'looks' : 'look' }} like bank or tax
            identifiers. VIVE will stamp whatever you map here; make sure the
            client means to collect it on this document.
          </div>
        }

        @if (autoMappedFieldCount > 0) {
          <p class="aet-file-hint">
            {{ autoMappedFieldCount }} of {{ fields.length }}
            {{ fields.length === 1 ? 'field' : 'fields' }} matched what VIVE
            already fills and
            {{ autoMappedFieldCount === 1 ? 'was' : 'were' }} mapped for you.
            Change any of them if this client's document means something
            different by that name.
          </p>
        }

        <div class="aet-mapping-summary">
          <span class="chip chip-success">{{ mappedFieldCount }} mapped</span>
          @if (unmappedFieldCount > 0) {
            <span class="chip chip-warning">
              {{ unmappedFieldCount }} unmapped
            </span>
          }
          @if (missingFieldCount > 0) {
            <span class="chip chip-error">
              {{ missingFieldCount }} missing
            </span>
          }
        </div>

        <div class="table-responsive aet-mapping-table">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">PDF field</th>
                <th scope="col">Type</th>
                <th scope="col">Fill with</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody [formGroup]="ESignTemplateForm">
              <ng-container formArrayName="fields">
                @for (field of fields.controls; track field; let i = $index) {
                  <tr [formGroupName]="i">
                    <td class="aet-field-name">
                      {{ field.controls.pdfFieldName.value }}
                    </td>
                    <td>
                      <!--
                        A widget of a kind the backend doesn't recognize is sent
                        as text, so its fingerprint matches what an admin saved
                        before. It still shouldn't be LABELLED text.
                      -->
                      {{
                        field.controls.takesNoValue.value &&
                        field.controls.fieldType.value !== 'signature'
                          ? 'no value'
                          : field.controls.fieldType.value
                      }}
                    </td>
                    <td>
                      <!--
                        A widget nothing is written into has no decision to
                        make: a signature is added when the vendor signs, and
                        prefill skips it. Only the defaulted BLANK hides the
                        picker. Any other saved source stays visible, so an old
                        "Ask the vendor" on a signature, which vive-v2 now
                        refuses, can still be seen and fixed.
                      -->
                      @if (
                        field.controls.takesNoValue.value &&
                        field.controls.source.value === 'BLANK'
                      ) {
                        <p class="aet-file-hint mb-0">
                          {{
                            field.controls.fieldType.value === 'signature'
                              ? "Holds the vendor's signature, added when they sign. Nothing to map."
                              : 'This field takes no value. Nothing to map.'
                          }}
                        </p>
                      } @else {
                        <!--
                        A picklist, never a text box. A typed field source is a
                        typo that prints blank on an executed contract with
                        nothing anywhere saying so.
                      -->
                        <select class="form-select" formControlName="source">
                          <option [ngValue]="null" disabled>
                            Choose a source…
                          </option>
                          @for (group of sourceGroupsForTemplate; track group) {
                            <optgroup [label]="group.label">
                              @for (option of group.options; track option) {
                                <option [ngValue]="option.value">
                                  {{ option.display }}
                                </option>
                              }
                            </optgroup>
                          }
                        </select>

                        @if (
                          needsParamFor(field.controls.source.value) === 'digit'
                        ) {
                          <input
                            type="text"
                            class="form-control mt-2"
                            formControlName="sourceParam"
                            placeholder="Which digit? e.g. 1"
                          />
                        }
                        @if (
                          needsParamFor(field.controls.source.value) ===
                          'companyType'
                        ) {
                          <input
                            type="text"
                            class="form-control mt-2"
                            formControlName="sourceParam"
                            placeholder="Which company type?"
                          />
                        }
                        @if (field.controls.source.value === 'STATIC') {
                          <input
                            type="text"
                            class="form-control mt-2"
                            formControlName="staticValue"
                            placeholder="Text to stamp"
                          />
                        }
                        <!--
                        Everything VPDW-4842 needs to render the vendor's form:
                        the label they read, the order it appears in, whether
                        they may skip it, and what counts as a valid answer.
                        Configured here because only the client knows what they
                        are asking for.
                      -->
                        @if (field.controls.source.value === 'VENDOR_INPUT') {
                          <div class="aet-vendor-field">
                            <label class="form-label" [for]="'aet-label-' + i">
                              Label the vendor sees
                            </label>
                            <input
                              [id]="'aet-label-' + i"
                              type="text"
                              class="form-control"
                              formControlName="displayLabel"
                              placeholder="e.g. Your title at the company"
                            />

                            <div class="aet-vendor-field-row">
                              <div>
                                <label
                                  class="form-label"
                                  [for]="'aet-order-' + i"
                                >
                                  Order
                                </label>
                                <input
                                  [id]="'aet-order-' + i"
                                  type="number"
                                  min="0"
                                  class="form-control"
                                  formControlName="displayOrder"
                                />
                              </div>

                              <!--
                              A length limit and a pattern only describe an
                              answer someone TYPES. A checkbox answers with one
                              agreed value, and a choice field answers with one
                              of the PDF's own export values — offering either
                              box here would invite a rule that refuses an
                              answer the document itself supplied.
                            -->
                              @if (
                                takesTypedAnswer(field.controls.fieldType.value)
                              ) {
                                <div>
                                  <label
                                    class="form-label"
                                    [for]="'aet-max-length-' + i"
                                  >
                                    Max length
                                  </label>
                                  <input
                                    [id]="'aet-max-length-' + i"
                                    type="number"
                                    min="1"
                                    class="form-control"
                                    formControlName="maxLength"
                                    placeholder="No limit"
                                  />
                                </div>
                              }
                            </div>

                            @if (
                              takesTypedAnswer(field.controls.fieldType.value)
                            ) {
                              <div>
                                <label
                                  class="form-label"
                                  [for]="'aet-validation-' + i"
                                >
                                  Validation
                                </label>
                                <select
                                  [id]="'aet-validation-' + i"
                                  class="form-select"
                                  formControlName="validationRegex"
                                >
                                  @for (
                                    rule of vendorInputValidationRules;
                                    track rule
                                  ) {
                                    <option [ngValue]="rule.value">
                                      {{ rule.display }}
                                    </option>
                                  }
                                </select>
                              </div>
                            }

                            <!--
                            One box per choice the FILE declares. The vendor
                            reads these; the PDF's own export values are shown
                            beside them only so an admin can tell which is
                            which. Leaving one blank is allowed and falls back
                            to the export value — it does not store an empty
                            name.
                          -->
                            @if (field.controls.options.value.length > 0) {
                              <div formGroupName="optionLabels">
                                <span class="form-label aet-option-heading">
                                  What each choice is called
                                </span>
                                @for (
                                  option of field.controls.options.value;
                                  track option;
                                  let o = $index
                                ) {
                                  <div class="aet-option-label-row">
                                    <label
                                      class="aet-option-value"
                                      [for]="'aet-option-' + i + '-' + o"
                                    >
                                      {{ option }}
                                    </label>
                                    <input
                                      [id]="'aet-option-' + i + '-' + o"
                                      type="text"
                                      class="form-control"
                                      [formControlName]="option"
                                      placeholder="e.g. Sole proprietor"
                                    />
                                  </div>
                                }
                              </div>
                            }

                            <div class="form-check">
                              <input
                                [id]="'aet-required-' + i"
                                type="checkbox"
                                class="form-check-input"
                                formControlName="isRequired"
                              />
                              <!--
                              Spelled out for a checkbox, because "must fill
                              in" reads as "must answer" and the rule is
                              stricter than that: a required checkbox the
                              vendor leaves clear is refused at submit.
                            -->
                              <label
                                class="form-check-label"
                                [for]="'aet-required-' + i"
                              >
                                {{
                                  field.controls.fieldType.value === 'checkbox'
                                    ? 'Must be ticked'
                                    : 'Must fill in'
                                }}
                              </label>
                            </div>
                          </div>
                        }
                      }
                    </td>
                    <td>
                      @if (!field.controls.isInPdf.value) {
                        <span class="chip chip-error">Not in PDF</span>
                      } @else if (field.controls.takesNoValue.value) {
                        <span class="chip chip-neutral">
                          {{
                            field.controls.fieldType.value === 'signature'
                              ? 'Signature'
                              : 'No value'
                          }}
                        </span>
                      } @else if (field.controls.source.value) {
                        <span class="chip chip-success">Mapped</span>
                      } @else {
                        <span class="chip chip-warning">Unmapped</span>
                      }
                    </td>
                  </tr>
                }
              </ng-container>
            </tbody>
          </table>
        </div>
      }
    </div>
  }
</div>

<div class="modal-footer">
  <div class="aet-footer w-100">
    <!--
      The count BEFORE the button, not in a toast afterwards. Saving a mapping
      moves the template's checksum, and every vendor measured against the old
      one is asked to sign again — there is no undo for that.
    -->
    @if (willSendFields && resignImpactCount) {
      <p class="aet-resign-impact mb-0">
        <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
        Saving a change to this mapping asks
        <strong>
          {{ resignImpactCount }}
          {{ resignImpactCount === 1 ? 'vendor' : 'vendors' }}
        </strong>
        to sign again.
      </p>
    }

    <div class="action-row justify-end">
      <button type="button" class="btn btn-ghost" (click)="close()">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary"
        [disabled]="unmappedFieldCount > 0"
        (click)="save()"
      >
        Save template
      </button>
    </div>
  </div>

  @if (unmappedFieldCount > 0) {
    <p class="aet-save-blocked w-100 mb-0">
      Every field needs a source before this can be saved. Pick
      <em>Leave blank</em> for any the client does not use — that records the
      decision instead of leaving a gap nobody can explain later.
    </p>
  }
</div>
